import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { OrderInfosResponse } from '~/api/queries/order'
import {
  type ApiResponseError,
  fetchApiWithToken,
  getApiViolations
} from '~/api/utils'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import type { AddressPayload } from '~/types/global'
import { toast } from '~/utils'
import { buildOrderContractFields } from '../utils'

type DuplicateOrderPayload = {
  contact: string
  qualifications: string[]
  jobTitle?: string
  address?: AddressPayload
  applicantWantedCount: number
  contractTerm?: number | null
  contractStart?: string | null
  contractEnd?: string | null
}

export const useDuplicateOrderMutation = () => {
  const queryClient = useQueryClient()

  return useMutation<
    OrderInfosResponse,
    ApiResponseError,
    { orderId: number; payload: DuplicateOrderPayload }
  >({
    mutationFn: async ({ orderId, payload }) => {
      const {
        contact,
        qualifications,
        jobTitle,
        address,
        applicantWantedCount,
        contractTerm,
        contractStart,
        contractEnd
      } = payload

      const data: any = {
        contact,
        qualifications: qualifications.map(
          (q) => `/v1/taxonomies/qualifications/${q}`
        ),
        applicantWantedCount: applicantWantedCount ?? 0,
        ...buildOrderContractFields({
          jobTitle,
          address,
          contractTerm,
          contractStart,
          contractEnd
        })
      }

      return fetchApiWithToken<OrderInfosResponse>({
        method: 'post',
        url: `/v1/orders/${orderId}/duplicate`,
        data
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      toast(
        'Commande dupliquée',
        'La commande a été dupliquée avec succès',
        ToastType.Success
      )
    },
    onError: (error) => {
      if (getApiViolations(error).length > 0) {
        return
      }
      toast(
        'Erreur lors de la duplication',
        'Une erreur est survenue lors de la duplication de la commande',
        ToastType.Danger
      )
    }
  })
}
